/**
 * The client list as CSV — exactly the rows and columns on screen.
 *
 * The portal's "Export" button did nothing at all: it rendered, it looked live, and clicking it had
 * no effect. This builds the file in the browser from data already loaded, so nothing is sent
 * anywhere, and it honours the current search and filters — what you export is what you were
 * looking at.
 *
 * Workflow data only (who, what stage, how far through, when last active). A client's financial
 * position is deliberately not here: this is the list an adviser wants for chasing work, and the
 * fact-find is encrypted per client precisely so it doesn't leak into places like this.
 */
import type { PartnerCase } from './PartnerPortal.tsx';

export const CLIENTS_CSV_HEADER = [
    'Client',
    'Type',
    'Profile status',
    'Profile %',
    'Steps done',
    'SOA status',
    'Last activity',
    'Access mode',
] as const;

/** Quote every field and double any embedded quote — a client name containing a comma would
 *  otherwise split itself across two columns. */
function csvCell(value: unknown): string {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
}

function profilePercent(c: PartnerCase): string {
    if (c.profileProgressPct == null) return '';
    return `${c.profileProgressIsEstimated ? '~' : ''}${c.profileProgressPct}%`;
}

function stepsDone(c: PartnerCase): string {
    if (c.profileCompletedSteps == null || c.profileTotalSteps == null) return '';
    return `${c.profileCompletedSteps} of ${c.profileTotalSteps}`;
}

/** Same test the table's Type column uses: the fact-find where it's loaded, else the display name
 *  (a couple's file is titled "Tom & Robyn Smith"). */
function isCouple(c: PartnerCase): boolean {
    return c.factFind?.adviceFor === 'couple' || Boolean(c.clientName && c.clientName.includes('&'));
}

function soaStatusLabel(c: PartnerCase): string {
    if (c.soaStatus === 'published') return 'Published';
    if (c.soaStatus === 'draft') return 'Draft';
    return 'None';
}

/** One row per case, in the column order of CLIENTS_CSV_HEADER. */
export function clientCsvRow(c: PartnerCase, profileStatusLabel: string): string[] {
    return [
        c.clientName,
        isCouple(c) ? 'Couple' : 'Single',
        profileStatusLabel,
        profilePercent(c),
        stepsDone(c),
        soaStatusLabel(c),
        c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString('en-AU') : '',
        c.accessMode === 'in_person' ? 'In-person' : 'Invited',
    ];
}

/** The complete CSV text, CRLF-delimited as the format expects. */
export function buildClientsCsv(cases: PartnerCase[], profileStatusLabel: (c: PartnerCase) => string): string {
    const rows = cases.map((c) => clientCsvRow(c, profileStatusLabel(c)));
    return [[...CLIENTS_CSV_HEADER], ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

/** Trigger the browser download. Separated from the string building so that stays testable. */
export function downloadClientsCsv(csv: string, filename: string): void {
    // A UTF-8 byte-order mark, so Excel opens the file as UTF-8 — without it, an accented name
    // comes out mangled.
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}
